export class ClinicalDashboardApiError extends Error {
	status: number;

	constructor(message: string, status: number) {
		super(message);
		this.name = "ClinicalDashboardApiError";
		this.status = status;
	}
}

export interface CopilotPatientReference {
	id: number;
	nome: string;
	foto: string | null;
	realizadaEm?: string | null;
}

export type AttentionPointStatus =
	| "IDENTIFICADO"
	| "SEM_ALERTA"
	| "DADOS_INSUFICIENTES";

export type AttentionPointLevel = "GRAVE" | "SIGNIFICATIVA" | "LEVE";

export interface CopilotAttentionPoint {
	status?: AttentionPointStatus;
	nivel?: AttentionPointLevel | null;
	percentual?: number | null;
	movimento?: string | null;
	ladoMaisFraco?: {
		codigo?: string | null;
		descricao?: string | null;
	} | null;
	texto?: string | null;
}

export interface CopilotSummaryData {
	RESUMO_TXT?: {
		titulo?: string | null;
		conteudo?: string | null;
	} | null;
	PONTO_ATENCAO?: CopilotAttentionPoint | null;
	ULTIMA_AVALIACAO?: string | null;
	[key: string]: unknown;
}

export interface OperationalPatientPage {
	items: CopilotPatientReference[];
	paginacao: {
		pagina: number;
		limite: number;
		temProxima: boolean;
	};
}

const post = async (
	path: string,
	token: string,
	body: Record<string, unknown>,
	signal?: AbortSignal
) => {
	const response = await fetch(`${process.env.REACT_APP_BASE_URL_V7}${path}`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify(body),
		signal,
	});

	let data: any = null;
	try {
		data = await response.json();
	} catch (_) {
		throw new ClinicalDashboardApiError(
			"A resposta do servidor não pôde ser interpretada.",
			response.status
		);
	}

	if (!response.ok || data?.status !== "success" || data?.code !== 0) {
		throw new ClinicalDashboardApiError(
			data?.message || "Não foi possível carregar os dados.",
			response.status
		);
	}

	return data;
};

export const fetchOperationalPatients = async ({
	token,
	search,
	page,
	limit,
	signal,
}: {
	token: string;
	search: string;
	page: number;
	limit: number;
	signal?: AbortSignal;
}): Promise<OperationalPatientPage> => {
	const body: Record<string, unknown> = { PAGINA: page, LIMITE: limit };
	if (search.trim()) body.BUSCA = search.trim();
	const data = await post(
		"dashboard/operacional/pacientes/buscar",
		token,
		body,
		signal
	);
	return data.pacientes;
};

export const fetchPatientRecordSummary = async (
	token: string,
	patientId: number,
	signal?: AbortSignal
) => {
	const data = await post(
		"dashboard/operacional/paciente/prontuario-resumo",
		token,
		{ ID_USUARIO: patientId },
		signal
	);
	return data.prontuario;
};

const copilotSummaryRequests = new Map<string, Promise<CopilotSummaryData>>();

const waitForRequest = <T>(request: Promise<T>, signal?: AbortSignal) => {
	if (!signal) return request;
	if (signal.aborted) {
		const error = new Error("Request aborted");
		error.name = "AbortError";
		return Promise.reject(error);
	}

	return new Promise<T>((resolve, reject) => {
		const abort = () => {
			const error = new Error("Request aborted");
			error.name = "AbortError";
			reject(error);
		};
		const cleanup = () => signal.removeEventListener("abort", abort);
		signal.addEventListener("abort", abort, { once: true });
		request.then(
			(data) => {
				cleanup();
				resolve(data);
			},
			(error) => {
				cleanup();
				reject(error);
			}
		);
	});
};

export const fetchCopilotSummary = (
	token: string,
	patientId: number,
	signal?: AbortSignal
) => {
	const requestKey = `${token}:${patientId}`;
	let request = copilotSummaryRequests.get(requestKey);
	if (!request) {
		request = post("ia/copiloto/resumo", token, {
			ID_USUARIO: patientId,
		})
			.then((data) => (data.resumo || {}) as CopilotSummaryData)
			.finally(() => copilotSummaryRequests.delete(requestKey));
		copilotSummaryRequests.set(requestKey, request);
	}
	return waitForRequest(request, signal);
};

export const fetchCopilotEvolution = async (
	token: string,
	patientId: number,
	signal?: AbortSignal
) => {
	const data = await post(
		"ia/copiloto/evolucao",
		token,
		{ ID_USUARIO: patientId },
		signal
	);
	return data.evolucao || null;
};

export const fetchFullPatientForDashboard = async (
	token: string,
	patientId: number,
	signal?: AbortSignal
) => {
	const data = await post(
		"syncs/usuarios_filtro",
		token,
		{
			TOKEN: token,
			inicio: 0,
			fim: 1,
			filtros: [
				{ tipo: "ID_USUARIO", valor: [patientId] },
				{ tipo: "USUARIO_EXCLUIDO", valor: "N" },
				{ tipo: "USUARIO_NIVEL", valor: [3] },
			],
		},
		signal
	);
	return Array.isArray(data.usuarios) && data.usuarios.length === 1
		? data.usuarios[0]
		: null;
};

export const isAbortError = (error: unknown) =>
	(error as any)?.name === "AbortError";
